import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import IconButton from '@material-ui/core/IconButton';
import MenuItem from '@material-ui/core/MenuItem';
import Menu from '@material-ui/core/Menu';
import Avatar from '@material-ui/core/Avatar';
import Tooltip from '@material-ui/core/Tooltip';
import Hidden from '@material-ui/core/Hidden';
import Switch from '@material-ui/core/Switch';
import Badge from '@material-ui/core/Badge';
import Grid from '@material-ui/core/Grid';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Divider from '@material-ui/core/Divider';
import ListSubheader from '@material-ui/core/ListSubheader';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import NotificationsIcon from '@material-ui/icons/Notifications';
import GpsOffIcon from '@material-ui/icons/GpsOff';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import AccountCircle from '@material-ui/icons/AccountCircle';
import TimerIcon from '@material-ui/icons/Timer';
import { useStyles } from './AppBarAvatarStyles';
import { Typography } from '@material-ui/core';

import { useResetRecoilState, useRecoilState, useRecoilValue } from 'recoil';
import { useQuery } from 'react-query';

import {
	userState,
	userDisplayState,
	themeState,
	isRestrictedState,
} from 'state';
import { VerifyUserToken, LogoutUser } from 'api';
import { Loader } from 'common/components';
import { whenRestrictionExpires } from 'utils';

const NOTIFICATION_MENU = 'notification_menu';
const PROFILE_MENU = 'profile_menu';

export const MemoizedAppBarAvatar = React.memo(
	function AppBarAvatar({ userAllowed, inAppName, sysAppname, url }) {
		const [user, setUser] = useRecoilState(userState);
		const [theme, setTheme] = useRecoilState(themeState);
		const { initials, username } = useRecoilValue(userDisplayState);

		const resetUser = useResetRecoilState(userState);

		const isRestricted = useRecoilValue(isRestrictedState);

		const [notifications, setNotifications] = useState([]);

		const [anchorEl, setAnchorEl] = useState(null);
		const [anchorNotify, setAnchorNotify] = useState(null);

		const classes = useStyles();

		const { isLoading, error, data } = useQuery('userToken', VerifyUserToken, {
			retry: 0,
			refetchOnWindowFocus: false,
		});

		const open = Boolean(anchorEl);
		const openNotify = Boolean(anchorNotify);

		// * Preveri, če je uporabnik že vpisan.
		useEffect(() => {
			if (error) {
				console.log(error.response);
			}

			if (data) {
				const {
					data: { Authenticated, Name, Surname, Token, UserName },
				} = data;

				setUser({
					isLogged: Authenticated,
					username: UserName,
					name: Name,
					surname: Surname,
					token: Token,
				});
			}
		}, [error, data, setUser]);

		const handleMenu = (e, type) => {
			if (type === NOTIFICATION_MENU) {
				setAnchorNotify(e.currentTarget);
			} else if (type === PROFILE_MENU) {
				setAnchorEl(e.currentTarget);
			}
		};

		const handleClose = () => {
			setAnchorNotify(null);
			setAnchorEl(null);
		};

		const handleLoginClick = () => {
			handleClose();
			const { REACT_APP_LOGIN_URL } = process.env;
			window.location.href = `${REACT_APP_LOGIN_URL}?a=${sysAppname}&returnFullUrl=${encodeURIComponent(
				window.location.href
			)}`;
		};

		const handleRegisterClick = () => {
			handleClose();
			const { REACT_APP_REGISTER_URL } = process.env;
			window.location.href = `${REACT_APP_REGISTER_URL}?a=${sysAppname}&returnFullUrl=${encodeURIComponent(
				window.location.href
			)}`;
		};

		const handleLogoutClick = async () => {
			try {
				const { data } = await LogoutUser();
				if (data === true) {
					resetUser();
				}
			} catch (error) {
				console.log(error);
			}
			handleClose();
		};

		const handleChange = (e) => {
			e.preventDefault();
			setTheme((prevState) => ({
				...prevState,
				palette: {
					...prevState.palette,
					type: prevState.palette.type === 'light' ? 'dark' : 'light',
				},
			}));
			handleClose();
		};

		useEffect(() => {
			const listOfNotifications = [];
			if (!inAppName)
				listOfNotifications.push({
					icon: <ErrorOutlineIcon />,
					text: 'Niste znotraj občine.',
				});
			if (!userAllowed)
				listOfNotifications.push({
					icon: <GpsOffIcon />,
					text: 'Niste dovolili dostopa do lokacijskih storitev.',
				});

			if (isRestricted) {
				listOfNotifications.push({
					icon: <TimerIcon />,
					text: `Obrazec bo ponovno aktiven kasneje.`,
				});
			}
			setNotifications(listOfNotifications);
		}, [inAppName, userAllowed, isRestricted, url]);

		return (
			<div>
				{isLoading && <Loader color="secondary" />}
				{!isLoading && (
					<>
						<Grid container>
							<Grid item className={classes.gridNotificationRow}>
								<IconButton
									aria-label="show notifications"
									color="inherit"
									onClick={(e) => handleMenu(e, NOTIFICATION_MENU)}>
									<Badge badgeContent={notifications.length} color="secondary">
										<NotificationsIcon />
									</Badge>
								</IconButton>
							</Grid>
							<Grid item>
								<Hidden smDown>
									<Typography 
										display="inline" 
										style={{ display: 'none' }} // TODO Always hidden, pregledat kodo, kje vse se sklicuje na ta gumb
										>{username}</Typography>
								</Hidden>
								<IconButton
									aria-label="account of current user"
									aria-controls="menu-appbar"
									aria-haspopup="true"
									onClick={(e) => handleMenu(e, PROFILE_MENU)}
									className={classes.avatarWhenSmall}
									color="inherit"
									style={{ display: 'none' }}  // TODO Always hidden, pregledat kodo, kje vse se sklicuje na ta gumb
									>
									{user.isLogged ? (
										<Tooltip title="Odjava" aria-label="odjava">
											<Avatar className={classes.avatarColor}>
												{initials}
											</Avatar>
										</Tooltip>
									) : (
										<Tooltip title="Prijava" aria-label="prijava">
											<AccountCircle className={classes.large} />
										</Tooltip>
									)}
								</IconButton>
								<Hidden mdUp>
									<Typography className={classes.nameWhenSmall}>
										{username}
									</Typography>
								</Hidden>
							</Grid>
						</Grid>
						<Menu
							id="menu-appbar"
							anchorEl={anchorEl}
							anchorOrigin={{
								vertical: 'top',
								horizontal: 'right',
							}}
							keepMounted
							transformOrigin={{
								vertical: 'top',
								horizontal: 'right',
							}}
							open={open}
							onClose={(e) => handleClose(PROFILE_MENU)}>
							{!user.isLogged ? (
								<div>
									<MenuItem onClick={handleLoginClick}>Prijava</MenuItem>
									<MenuItem onClick={handleRegisterClick}>
										Registracija
									</MenuItem>
								</div>
							) : (
								<div>
									<MenuItem>
										<FormControlLabel
											value="start"
											control={
												<Switch
													color="primary"
													checked={theme.palette.type === 'dark'}
													onChange={handleChange}
												/>
											}
											label="Temni način"
											labelPlacement="start"
										/>
									</MenuItem>

									<MenuItem onClick={handleLogoutClick}>Odjava</MenuItem>
								</div>
							)}
						</Menu>
						<Menu
							id="menu-appbar-notifications"
							anchorEl={anchorNotify}
							anchorOrigin={{
								vertical: 'top',
								horizontal: 'right',
							}}
							keepMounted
							transformOrigin={{
								vertical: 'top',
								horizontal: 'right',
							}}
							open={openNotify}
							onClose={(e) => handleClose(NOTIFICATION_MENU)}>
							<div>
								<List
									component="nav"
									subheader={
										<ListSubheader component="div" id="nested-list-subheader">
											{notifications.length > 0 ? 'Obvestila' : 'Ni obvestil'}
										</ListSubheader>
									}>
									{notifications.map((notification, index) => (
										<React.Fragment key={index}>
											<ListItem>
												<ListItemIcon>{notification.icon}</ListItemIcon>
												<ListItemText primary={notification.text} />
											</ListItem>
											{index < notifications.length - 1 ? <Divider /> : null}
										</React.Fragment>
									))}
								</List>
							</div>
						</Menu>
					</>
				)}
			</div>
		);
	},
	(prevProps, nextProps) =>
		prevProps.userAllowed === nextProps.userAllowed &&
		prevProps.inAppName === nextProps.inAppName &&
		prevProps.sysAppname === nextProps.sysAppname
);

MemoizedAppBarAvatar.propTypes = {
	userAllowed: PropTypes.bool,
	inAppName: PropTypes.bool,
	sysAppname: PropTypes.string,
	hasGeofence: PropTypes.bool,
	hasGeolocation: PropTypes.bool,
};
